import type { FunctionMeta } from '../function.model';

/**
 * Quadratic function in standard form: f(x) = ax² + bx + c
 *
 * - a: quadratic coefficient (stretch/compression and reflection)
 * - b: linear coefficient
 * - c: constant term (y-intercept)
 */
export interface QuadraticParams {
  a: number;
  b: number;
  c: number;
}

/**
 * Convert standard form parameters (a, b, c) to vertex form (h, k).
 *
 * h = -b / (2a)
 * k = c - b² / (4a)
 */
export function standardToVertex(a: number, b: number, c: number): { h: number; k: number } {
  const h = -b / (2 * a);
  const k = c - (b * b) / (4 * a);
  return { h, k };
}

export const QUADRATIC_META: FunctionMeta<QuadraticParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'extrema',
    'tangent',
    'secant',
    'derivative',
    'axisOfSymmetry',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: {
      xParam: 'b',
      yParam: 'c',
      toPosition: (p) => {
        const h = -p['b'] / (2 * p['a']);
        const k = p['c'] - (p['b'] * p['b']) / (4 * p['a']);
        return [h, k];
      },
      fromPosition: (x, y, p) => ({
        b: -2 * p['a'] * x,
        c: p['a'] * x * x + y,
      }),
    },
    'translate-x': {
      xParam: 'b',
      yParam: 'c',
      toPosition: (p) => {
        const h = -p['b'] / (2 * p['a']);
        const k = p['c'] - (p['b'] * p['b']) / (4 * p['a']);
        return [h, k];
      },
      fromPosition: (x, _y, p) => {
        const currentK = p['c'] - (p['b'] * p['b']) / (4 * p['a']);
        return {
          b: -2 * p['a'] * x,
          c: p['a'] * x * x + currentK,
        };
      },
    },
    'translate-y': {
      xParam: 'b',
      yParam: 'c',
      toPosition: (p) => {
        const h = -p['b'] / (2 * p['a']);
        const k = p['c'] - (p['b'] * p['b']) / (4 * p['a']);
        return [h, k];
      },
      fromPosition: (_x, y, p) => {
        const currentH = -p['b'] / (2 * p['a']);
        return {
          b: -2 * p['a'] * currentH,
          c: p['a'] * currentH * currentH + y,
        };
      },
    },
  },
  defaultParams: {
    a: 1,
    b: 0,
    c: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [-20, 20],
    c: [-20, 20],
  },
};
